import { useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/utils/formatters';
import { FUND_MEMBER_TYPE_LABELS } from '@/types/fundMember';
import { PhoneEditDialog } from './PhoneEditDialog';
import type { CarryProfile } from '../services/carryService';

function DetailRow({
  label,
  value,
  action,
}: {
  label: string;
  value: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-b-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="flex items-center gap-2 text-sm font-medium text-foreground">
        {value}
        {action}
      </dd>
    </div>
  );
}

function heldThroughLabel(memberType: CarryProfile['member_type']) {
  switch (memberType) {
    case 'MEMBER':
      return 'Directly';
    case 'DR_HOLDER':
      return 'Depositary receipts (STAK)';
  }
}

export function CarryProfileCard({ profile }: { profile: CarryProfile }) {
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your carry details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl>
          <DetailRow label="Name" value={profile.full_name} />
          <DetailRow label="Email" value={profile.email || 'Not set'} />
          <DetailRow
            label="Phone"
            value={profile.phone || 'Not set'}
            action={
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -my-1 -mr-1.5"
                aria-label="Edit phone number"
                onClick={() => setIsEditingPhone(true)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            }
          />
          <DetailRow
            label="Type"
            value={FUND_MEMBER_TYPE_LABELS[profile.member_type]}
          />
          <DetailRow
            label="Held through"
            value={heldThroughLabel(profile.member_type)}
          />
          <DetailRow
            label="Last login"
            value={
              profile.last_login_at
                ? formatDateTime(profile.last_login_at)
                : 'Never'
            }
          />
        </dl>
      </CardContent>

      {isEditingPhone && (
        <PhoneEditDialog
          open
          onOpenChange={setIsEditingPhone}
          currentPhone={profile.phone}
        />
      )}
    </Card>
  );
}
